export interface ParsedCSVData {
  headers: string[];
  rows: string[][];
  isValid: boolean;
}

export interface ChartableData {
  xColumn: string;
  yColumns: string[];
  data: Array<{ [key: string]: string | number }>;
  axisGroups?: {
    primary: string[];
    secondary: string[];
  };
}

const CANDIDATE_DELIMITERS = [',', ';', '\t', '|'];

// Detect if a user prompt is asking for a chart or visualization.
export const isVisualizationRequest = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return /(plot|graph|chart|visuali[sz]e|trend|time series|line chart|bar chart|scatter)/.test(normalized);
};

const splitCSVLine = (line: string, delimiter: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      const nextChar = line[i + 1];
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  result.push(current.trim());
  return result;
};

const detectDelimiterFromLines = (lines: string[]): string | null => {
  const nonEmptyLines = lines.map(line => line.trim()).filter(line => line.length > 0);
  if (nonEmptyLines.length === 0) return null;

  let bestDelimiter: string | null = null;
  let bestScore = 0;

  for (const delimiter of CANDIDATE_DELIMITERS) {
    const fieldCounts = nonEmptyLines.map(line => splitCSVLine(line, delimiter).length);
    const validCounts = fieldCounts.filter(count => count >= 2);
    if (validCounts.length < Math.min(3, nonEmptyLines.length)) continue;

    const countMap = new Map<number, number>();
    validCounts.forEach(count => countMap.set(count, (countMap.get(count) ?? 0) + 1));
    const [modeCount, modeFrequency] = Array.from(countMap.entries()).sort((a, b) => b[1] - a[1])[0];

    const score = modeFrequency * modeCount;
    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delimiter;
    }
  }

  return bestDelimiter;
};

// Extract unit from column name (e.g., "Price($/MWh)" -> "$/MWh")
const extractUnit = (columnName: string): string | null => {
  const unitMatch = columnName.match(/\(([^)]+)\)/);
  return unitMatch ? unitMatch[1] : null;
};

// Group columns by their units for dual-axis support
const groupColumnsByUnits = (columns: string[]): { primary: string[]; secondary: string[] } => {
  const columnUnits = columns.map(col => ({ column: col, unit: extractUnit(col) }));

  // Group by units
  const unitGroups: { [unit: string]: string[] } = {};
  const noUnitColumns: string[] = [];

  columnUnits.forEach(({ column, unit }) => {
    if (unit) {
      if (!unitGroups[unit]) {
        unitGroups[unit] = [];
      }
      unitGroups[unit].push(column);
    } else {
      noUnitColumns.push(column);
    }
  });

  const units = Object.keys(unitGroups);

  if (units.length <= 1) {
    // Single unit or no units - all on primary axis
    return { primary: columns, secondary: [] };
  }

  // Multiple units - split between axes
  const primaryUnit = units[0];
  const primary = [...unitGroups[primaryUnit], ...noUnitColumns];
  const secondary = units.slice(1).flatMap(unit => unitGroups[unit]);

  return { primary, secondary };
};

// Split columns onto separate axes when value ranges differ by > 100x
const splitByScale = (
  columns: string[],
  data: Array<{ [key: string]: string | number }>
): { primary: string[]; secondary: string[] } => {
  if (columns.length < 2) return { primary: columns, secondary: [] };

  const ranges = columns.map(col => {
    const values = data
      .map(row => (typeof row[col] === 'number' ? (row[col] as number) : null))
      .filter((v): v is number => v !== null);
    if (values.length === 0) return { col, range: 0 };
    return { col, range: Math.max(...values) - Math.min(...values) };
  });

  const maxRange = Math.max(...ranges.map(r => r.range));
  // minRange is Infinity when no column has a positive range (all flat/non-numeric),
  // which makes the ratio 0 and correctly falls through to the single-axis result.
  const minRange = Math.min(...ranges.filter(r => r.range > 0).map(r => r.range));

  if (!Number.isFinite(minRange) || maxRange / minRange < 100) return { primary: columns, secondary: [] };

  // Columns whose range is < 1% of the max range go to secondary
  const threshold = maxRange / 100;
  const primary = ranges.filter(r => r.range >= threshold).map(r => r.col);
  const secondary = ranges.filter(r => r.range < threshold).map(r => r.col);

  return { primary, secondary };
};

// Detect if message contains CSV-like data
export const detectCSVInMessage = (message: string): boolean => {
  // Look for <csv> tags first (preferred method)
  if (/<csv(?:\s+[^>]*)?>/i.test(message) && /<\/csv>/i.test(message)) {
    return true;
  }

  return extractCSVFromMessage(message) !== null;
};

// Extract CSV data from message
export const extractCSVFromMessage = (message: string): { csvString: string; title?: string } | null => {
  // Try to extract from <csv> tags with optional title attribute (prefer valid, non-empty blocks)
  const csvTagRegex = /<csv(?:\s+title="([^"]*)")?\s*>([\s\S]*?)<\/csv>/gi;
  const taggedMatches: Array<{ csvString: string; title?: string }> = [];
  let match: RegExpExecArray | null = null;
  while ((match = csvTagRegex.exec(message)) !== null) {
    const csvString = match[2]?.trim() ?? '';
    taggedMatches.push({ csvString, title: match[1] || undefined });
  }

  const validTaggedMatches = taggedMatches.filter(({ csvString }) => {
    const parsed = parseCSV(csvString);
    return parsed.isValid && parsed.headers.length >= 2;
  });
  if (validTaggedMatches.length > 0) {
    const bestMatch = validTaggedMatches.reduce((best, current) =>
      current.csvString.split('\n').length > best.csvString.split('\n').length ? current : best
    );
    return bestMatch;
  }

  const lines = message.split('\n');
  const blocks: Array<{ csvString: string }> = [];
  let currentBlock: string[] = [];

  const isDelimitedLine = (line: string): boolean => {
    const trimmed = line.trim();
    if (trimmed.length === 0) return false;
    return CANDIDATE_DELIMITERS.some(delimiter => splitCSVLine(trimmed, delimiter).length >= 2);
  };

  // Matches markdown table separator rows like |---|---| or | :--- | ---: |
  const isMarkdownTableSeparator = (line: string): boolean =>
    /^\|(\s*:?-+:?\s*\|)+$/.test(line.trim());

  const isMarkdownTable = (blockLines: string[]): boolean =>
    blockLines.some(isMarkdownTableSeparator);

  lines.forEach((line) => {
    if (isDelimitedLine(line)) {
      currentBlock.push(line.trim());
    } else {
      if (currentBlock.length >= 2 && !isMarkdownTable(currentBlock)) {
        blocks.push({ csvString: currentBlock.join('\n') });
      }
      currentBlock = [];
    }
  });
  if (currentBlock.length >= 2 && !isMarkdownTable(currentBlock)) {
    blocks.push({ csvString: currentBlock.join('\n') });
  }

  if (blocks.length === 0) return null;

  const validBlocks = blocks
    .map(block => ({ ...block, parsed: parseCSV(block.csvString) }))
    .filter(block => block.parsed.isValid && block.parsed.headers.length >= 2);

  if (validBlocks.length === 0) return null;

  const bestBlock = validBlocks.reduce((best, current) =>
    current.parsed.rows.length > best.parsed.rows.length ? current : best
  );

  return { csvString: bestBlock.csvString, title: undefined };
};

// Extract all CSV blocks from message (prefers tagged blocks if present)
export const extractAllCSVFromMessage = (message: string): Array<{ csvString: string; title?: string }> => {
  const csvTagRegex = /<csv(?:\s+title="([^"]*)")?\s*>([\s\S]*?)<\/csv>/gi;
  const taggedMatches: Array<{ csvString: string; title?: string }> = [];
  let match: RegExpExecArray | null = null;

  while ((match = csvTagRegex.exec(message)) !== null) {
    const csvString = match[2]?.trim() ?? '';
    if (csvString.length === 0) continue;
    taggedMatches.push({ csvString, title: match[1] || undefined });
  }

  if (taggedMatches.length > 0) {
    return taggedMatches;
  }

  const fallback = extractCSVFromMessage(message);
  return fallback && fallback.csvString ? [fallback] : [];
};

// Parse CSV string into structured data
export const parseCSV = (csvString: string): ParsedCSVData => {
  const lines = csvString.trim().split('\n').filter(line => line.trim().length > 0);
  
  if (lines.length < 2) {
    return { headers: [], rows: [], isValid: false };
  }

  const delimiter = detectDelimiterFromLines(lines);
  if (!delimiter) {
    return { headers: [], rows: [], isValid: false };
  }

  const sanitizeCell = (value: string): string => value.trim().replace(/^["']|["']$/g, '');

  // Parse header row
  const headers = splitCSVLine(lines[0], delimiter).map(sanitizeCell);
  
  // Parse data rows
  const rows = lines.slice(1).map(line => {
    return splitCSVLine(line, delimiter).map(sanitizeCell);
  });
  
  // Validate that all rows have same number of columns as headers
  const isValid = rows.every(row => row.length === headers.length);
  
  return { headers, rows, isValid };
};

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const normalized = trimmed.replace(/,/g, '');
  if (!/^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const isDateLike = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length < 6) return false;
  const parsed = Date.parse(trimmed);
  return !Number.isNaN(parsed);
};

// Convert parsed CSV to chartable format
export const prepareChartData = (parsed: ParsedCSVData): ChartableData | null => {
  if (!parsed.isValid || parsed.headers.length < 2) return null;
  
  // Find numeric columns
  const numericColumns: string[] = [];
  const sampleSize = Math.min(20, parsed.rows.length);
  const dateColumns: string[] = [];
  
  parsed.headers.forEach((header, colIndex) => {
    const values = parsed.rows.slice(0, sampleSize).map(row => row[colIndex]);
    const numericValues = values.map(val => parseNumber(val)).filter(val => val !== null);
    const dateValues = values.filter(val => isDateLike(val));

    if (dateValues.length >= sampleSize * 0.6) {
      dateColumns.push(header);
    }
    if (numericValues.length >= Math.max(2, sampleSize * 0.6)) { // 60% numeric threshold
      numericColumns.push(header);
    }
  });
  
  if (numericColumns.length === 0) return null;
  
  // Use first column as X-axis, numeric columns as Y-axis
  const xColumn = parsed.headers[0];
  const yColumns = numericColumns.filter(col => col !== xColumn && !dateColumns.includes(col));
  
  if (yColumns.length === 0) {
    // If first column is numeric, use indices as X and first column as Y
    const data = parsed.rows.map((row, index) => {
      const result: { [key: string]: string | number } = { index: index };
      parsed.headers.forEach((header, colIndex) => {
        const value = row[colIndex];
        const parsedNumber = parseNumber(value);
        result[header] = parsedNumber ?? value;
      });
      return result;
    });
    
    return {
      xColumn: 'index',
      yColumns: [parsed.headers[0]],
      data
    };
  }
  
  // Convert rows to objects
  const data = parsed.rows.map(row => {
    const result: { [key: string]: string | number } = {};
    parsed.headers.forEach((header, colIndex) => {
      const value = row[colIndex];
      const parsedNumber = parseNumber(value);
      result[header] = parsedNumber ?? value;
    });
    return result;
  });
  
  // Group columns by units for dual-axis support, fall back to scale-based split
  const unitGroups = groupColumnsByUnits(yColumns);
  const axisGroups = unitGroups.secondary.length > 0 ? unitGroups : splitByScale(yColumns, data);
  
  return { xColumn, yColumns, data, axisGroups };
};

// Main function to extract and prepare chart data from message
export const extractChartDataFromMessage = (message: string) => {
  if (!detectCSVInMessage(message)) return null;
  
  const csvResult = extractCSVFromMessage(message);
  if (!csvResult) return null;
  
  const parsed = parseCSV(csvResult.csvString);
  if (!parsed.isValid) return null;
  
  const chartData = prepareChartData(parsed);
  if (!chartData) return null;
  
  // Remove the entire CSV block including tags from the message
  let originalMessage = message;

  // Try to remove <csv> tags with content
  const csvTagPattern = /<csv(?:\s+title="[^"]*")?\s*>[\s\S]*?<\/csv>/;
  if (csvTagPattern.test(originalMessage)) {
    originalMessage = originalMessage.replace(csvTagPattern, '').trim();
  } else {
    // Fallback: remove just the CSV string
    originalMessage = originalMessage.replace(csvResult.csvString, '').trim();
  }

  return {
    csvString: csvResult.csvString,
    title: csvResult.title,
    parsed,
    chartData,
    originalMessage
  };
};
// Extract multiple charts data from message.
export const extractChartsDataFromMessage = (message: string) => {
  if (!detectCSVInMessage(message)) return null;
  // Extract all CSV blocks
  const csvBlocks = extractAllCSVFromMessage(message);
  if (csvBlocks.length === 0) return null;
  // Parse and prepare chart data for each block
  const charts = csvBlocks
    .map((block) => {
      const parsed = parseCSV(block.csvString);
      if (!parsed.isValid) return null;
      const chartData = prepareChartData(parsed);
      if (!chartData) return null;
      return {
        csvString: block.csvString,
        title: block.title,
        parsed,
        chartData
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
  // Return null if no valid charts found
  if (charts.length === 0) return null;

  // Remove all CSV blocks including tags from the message
  let originalMessage = message;
  const csvTagPattern = /<csv(?:\s+title="[^"]*")?\s*>[\s\S]*?<\/csv>/gi;
  if (csvTagPattern.test(originalMessage)) {
    originalMessage = originalMessage.replace(csvTagPattern, '').trim();
  } else {
    charts.forEach((chart) => {
      originalMessage = originalMessage.replace(chart.csvString, '').trim();
    });
  }

  return { charts, originalMessage };
};